import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import GoogleAuthButton from "@/components/GoogleAuthButton";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-obsidian text-bone">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-3 h-3 bg-intl" />
          <span className="font-app font-bold">AI Visibility OS</span>
        </Link>
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-bone/60">// status</div>
          <h2 className="mt-4 font-marketing text-5xl tracking-tighter leading-none">
            The internet got rebuilt.<br /><span className="text-intl">Your site didn't.</span>
          </h2>
          <p className="mt-8 text-bone/70 max-w-md">Welcome back. Pick up where you left off.</p>
        </div>
        <div className="font-mono text-xs text-bone/50">v1.0 // built for AI search</div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-12">
        <form onSubmit={onSubmit} className="w-full max-w-md" data-testid="login-form">
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">/ login</div>
          <h1 className="mt-4 font-marketing text-5xl tracking-tight">Welcome back.</h1>

          <div className="mt-10 space-y-5">
            <div>
              <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Email</label>
              <input
                type="email"
                value={email}
                data-testid="login-email-input"
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 w-full bg-card border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-intl"
              />
            </div>
            <div>
              <label className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Password</label>
              <input
                type="password"
                value={password}
                data-testid="login-password-input"
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1 w-full bg-card border border-border px-4 py-3 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-intl"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={busy}
            data-testid="login-submit-button"
            className="mt-8 w-full py-4 bg-obsidian text-bone font-app font-bold uppercase tracking-wider hover:bg-intl transition-colors disabled:opacity-60"
          >
            {busy ? "Authenticating…" : "Login"}
          </button>

          <div className="mt-6 flex items-center gap-3" aria-hidden="true">
            <div className="h-px flex-1 bg-border" />
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <div className="mt-6">
            <GoogleAuthButton label="Continue with Google" testid="login-google-button" />
          </div>

          <div className="mt-6 text-sm text-muted-foreground">
            Don't have an account?{" "}
            <Link to="/register" data-testid="goto-register-link" className="text-obsidian font-medium underline underline-offset-4">Get your free score</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
